import { type HistoryPoint, type PortfolioListItem } from './portfolio-list';
import { portfolioListFixture as list } from './portfolio-list.fixture';

/**
 * 포트폴리오 목록의 매출 · 기업가치 추이(Sparkline, DD-22 2). 기존 서버(`investment-set.response.ts`)의 자료 모양을 따른다:
 * - `revenueHistory` = 리포트의 **4분기** 매출 합계(`revenueTotal`)만, 해마다 한 점. 날짜는 그 해 4분기 첫날(`YYYY-10-01`,
 *   서버의 `new Date(year, 9)`). 값이 없는(null) 해는 빠진다.
 * - `stockPriceHistory` = 투자 건들의 변동 내역(`history`)을 날짜순으로 모은 **총 기업가치**(`totalValuationAmount`).
 *   같은 날짜는 큰 값 하나, 0 · 빈 값은 빠진다. 날짜는 변동일이라 간격이 고르지 않다.
 * 점 수에 상한은 없다(기존도 받은 만큼 그린다). 자료가 없으면 빈 배열 → 칸은 비운다.
 */
const RECO_REVENUE: HistoryPoint[] = [
  { amount: 3_200_000_000, when: '2019-10-01' },
  { amount: 4_100_000_000, when: '2020-10-01' },
  { amount: 3_800_000_000, when: '2021-10-01' },
  { amount: 6_900_000_000, when: '2022-10-01' },
  { amount: 9_400_000_000, when: '2023-10-01' },
];

const RECO_VALUATION: HistoryPoint[] = [
  { amount: 25_000_000_000, when: '2021-03-15' },
  { amount: 48_000_000_000, when: '2022-08-01' },
  { amount: 52_000_000_000, when: '2023-05-10' },
  { amount: 47_000_000_000, when: '2024-02-20' },
  { amount: 61_000_000_000, when: '2024-06-30' },
];

const SUPERBIN_REVENUE: HistoryPoint[] = [
  { amount: 5_600_000_000, when: '2020-10-01' },
  { amount: 7_300_000_000, when: '2021-10-01' },
  { amount: 6_100_000_000, when: '2022-10-01' },
  { amount: 8_800_000_000, when: '2023-10-01' },
];

const SUPERBIN_VALUATION: HistoryPoint[] = [
  { amount: 30_000_000_000, when: '2020-11-02' },
  { amount: 42_000_000_000, when: '2022-06-30' },
  { amount: 93_000_000_000, when: '2024-06-30' },
];

const DOCTORNOW_REVENUE: HistoryPoint[] = [
  { amount: 1_200_000_000, when: '2021-10-01' },
  { amount: 2_700_000_000, when: '2022-10-01' },
  { amount: 2_300_000_000, when: '2023-10-01' },
];

const DOCTORNOW_VALUATION: HistoryPoint[] = [
  { amount: 64_000_000_000, when: '2022-04-18' },
  { amount: 64_000_000_000, when: '2024-06-30' },
];

/** 부분회수 케이스 A · 다음 펀드 줄: 회사 단위 자료라 첫 줄과 같다(병합 칸) */
const recoFund1: PortfolioListItem = {
  ...list.recoFund1,
  revenueHistory: RECO_REVENUE,
  stockPriceHistory: RECO_VALUATION,
};
const recoFund2: PortfolioListItem = {
  ...list.recoFund2,
  revenueHistory: RECO_REVENUE,
  stockPriceHistory: RECO_VALUATION,
};
const superbin: PortfolioListItem = {
  ...list.superbin,
  revenueHistory: SUPERBIN_REVENUE,
  stockPriceHistory: SUPERBIN_VALUATION,
};
const doctornow: PortfolioListItem = {
  ...list.doctornow,
  revenueHistory: DOCTORNOW_REVENUE,
  stockPriceHistory: DOCTORNOW_VALUATION,
};
/** 감액 케이스 A(완전손상): 리포트 · 변동 내역 자료 없음 → 두 칸 모두 비운다 */
const testworks: PortfolioListItem = {
  ...list.testworks,
  revenueHistory: [],
  stockPriceHistory: [],
};

export const portfolioTrendFixture = {
  /** 추이 자료만(Sparkline 견본) */
  revenue: RECO_REVENUE,
  valuation: RECO_VALUATION,
  /** 줄 하나씩 */
  recoFund1,
  recoFund2,
  superbin,
  doctornow,
  testworks,
  /** portfolioListFixture 의 all · mine · fund 와 같은 줄에 추이 자료를 넣은 것 */
  all: [recoFund1, recoFund2, superbin, doctornow, testworks],
  mine: [recoFund1, recoFund2, superbin],
  fund: [recoFund1, superbin, doctornow, testworks],
} satisfies Record<string, HistoryPoint[] | PortfolioListItem | PortfolioListItem[]>;
